/**
 * Signed distance field from an antialiased coverage mask.
 *
 * Base algorithm: Felzenszwalb & Huttenlocher, "Distance Transforms of Sampled
 * Functions" (Theory of Computing, 2012) — the exact Euclidean distance transform as
 * a 1D lower-envelope-of-parabolas sweep along rows, then along columns, O(n) in the
 * number of pixels.
 *
 * Three implementation details carry the performance, and matter at 2000×2000 where a
 * naive version lands around 260 ms:
 *
 *  1. The *first* sweep runs on a binary signal (a pixel is either a seed or it is
 *     not). The lower envelope degenerates to "distance to the nearest seed in this
 *     row", which a two-direction linear scan computes exactly with no divisions at
 *     all. Only the second sweep, whose input is arbitrary, needs the full envelope.
 *  2. That first sweep writes its result *transposed*, so the column sweep reads
 *     contiguous memory instead of striding by `width` through a 16 MB buffer, and no
 *     separate transpose pass is needed.
 *  3. Scratch buffers are pooled per size; a rebuild would otherwise allocate and
 *     zero tens of megabytes, which costs the same order as the arithmetic.
 *
 * On top of the transform we apply a sub-pixel correction derived from the
 * antialiased coverage (as in Gustavson & Strand, "Anti-aliased Euclidean distance
 * transform"). A thresholded EDT quantises distances to whole pixels near the edge,
 * which shows up as visible stair-stepping in every technique that follows the
 * gradient; using `0.5 - coverage` in the one-pixel band around the contour removes it.
 */

const INF = 1e20;

interface Scratch {
  width: number;
  height: number;
  /** Transposed squared distances: `[x * height + y]`. */
  at: Float32Array;
  bt: Float32Array;
  /** Per-row working buffers for the linear scan. */
  rowA: Float32Array;
  rowB: Float32Array;
  /** Envelope scratch for the column sweep. */
  d: Float64Array;
  colA: Float64Array;
  v: Int32Array;
  z: Float64Array;
  /** Per-column flag: does this column contain any inside pixel at all? */
  colHasInside: Uint8Array;
}

let scratch: Scratch | null = null;

function getScratch(width: number, height: number): Scratch {
  if (scratch && scratch.width === width && scratch.height === height) return scratch;
  const n = width * height;
  const maxDim = Math.max(width, height);
  scratch = {
    width,
    height,
    at: new Float32Array(n),
    bt: new Float32Array(n),
    rowA: new Float32Array(maxDim),
    rowB: new Float32Array(maxDim),
    d: new Float64Array(maxDim),
    colA: new Float64Array(maxDim),
    v: new Int32Array(maxDim),
    z: new Float64Array(maxDim + 1),
    colHasInside: new Uint8Array(width),
  };
  return scratch;
}

/** Releases the pooled buffers; worth calling when leaving the studio for a while. */
export function releaseSdfScratch(): void {
  scratch = null;
}

/**
 * Lower envelope of parabolas over `n` samples read from `buf[off + i]`, result in `d`.
 * This is the general Felzenszwalb 1D transform, used for the second sweep.
 */
function envelope1d(buf: Float32Array, off: number, n: number, s: Scratch): void {
  const { d, v, z } = s;
  let k = 0;
  v[0] = 0;
  z[0] = -INF;
  z[1] = INF;

  for (let q = 1; q < n; q++) {
    const fq = buf[off + q] + q * q;
    let vk = v[k];
    let s1 = (fq - (buf[off + vk] + vk * vk)) / (2 * q - 2 * vk);
    while (s1 <= z[k]) {
      k--;
      vk = v[k];
      s1 = (fq - (buf[off + vk] + vk * vk)) / (2 * q - 2 * vk);
    }
    k++;
    v[k] = q;
    z[k] = s1;
    z[k + 1] = INF;
  }

  k = 0;
  for (let q = 0; q < n; q++) {
    while (z[k + 1] < q) k++;
    const dx = q - v[k];
    d[q] = dx * dx + buf[off + v[k]];
  }
}

export interface SdfResult {
  /** Signed distance in pixels. Negative inside the glyphs, positive outside. */
  sdf: Float32Array;
  width: number;
  height: number;
  /** Largest positive distance found — useful for normalising debug views. */
  maxOutside: number;
  /** Largest depth inside the glyphs, as a positive number. */
  maxInside: number;
}

export function computeSdf(alpha: Uint8Array, width: number, height: number): SdfResult {
  const n = width * height;
  const s = getScratch(width, height);
  const { at, bt, rowA, rowB, d, colA, colHasInside } = s;

  const sdf = new Float32Array(n);
  const limit = width + height;
  colHasInside.fill(0);

  // ── Pass 1: per-row distance to the nearest seed, written transposed ──────
  //
  // `a` measures distance to the nearest *inside* pixel, `b` to the nearest
  // *outside* pixel. Both are derived from the same threshold test, so one loop
  // produces both. A row with no seed of a kind gets INF, which the column sweep
  // then resolves from neighbouring rows.
  let hasInside = false;
  let hasOutside = false;

  for (let y = 0; y < height; y++) {
    const row = y * width;

    let lastInside = -1;
    let lastOutside = -1;
    for (let x = 0; x < width; x++) {
      if (alpha[row + x] >= 128) {
        lastInside = x;
        hasInside = true;
      } else {
        lastOutside = x;
        hasOutside = true;
      }
      rowA[x] = lastInside < 0 ? INF : x - lastInside;
      rowB[x] = lastOutside < 0 ? INF : x - lastOutside;
    }

    lastInside = -1;
    lastOutside = -1;
    for (let x = width - 1; x >= 0; x--) {
      if (alpha[row + x] >= 128) {
        lastInside = x;
        colHasInside[x] = 1;
      } else lastOutside = x;
      if (lastInside >= 0) {
        const backwards = lastInside - x;
        if (backwards < rowA[x]) rowA[x] = backwards;
      }
      if (lastOutside >= 0) {
        const backwards = lastOutside - x;
        if (backwards < rowB[x]) rowB[x] = backwards;
      }
      // Square here — the column sweep works in squared distances throughout.
      const i = x * height + y;
      const a = rowA[x];
      const b = rowB[x];
      at[i] = a >= INF ? INF : a * a;
      bt[i] = b >= INF ? INF : b * b;
    }
  }

  // Degenerate masks (no glyphs at all, or a fully covered canvas) have no contour to
  // measure against; saturate rather than propagate the INF sentinel.
  if (!hasInside || !hasOutside) {
    const value = hasInside ? -limit : limit;
    sdf.fill(value);
    return { sdf, width, height, maxOutside: hasInside ? 0 : limit, maxInside: hasInside ? limit : 0 };
  }

  // ── Pass 2: envelope sweep down each (now contiguous) column, combined with
  //           the un-transpose and the sub-pixel refinement ──────────────────
  //
  // Doing all three per column keeps the two 16 MB distance buffers out of a third
  // full traversal, and lets columns that contain no glyph skip the inside transform
  // entirely — for typical type that is a large share of the canvas.
  let maxOutside = 0;
  let maxInside = 0;

  for (let x = 0; x < width; x++) {
    const col = x * height;

    envelope1d(at, col, height, s);
    colA.set(d.subarray(0, height));

    const anyInside = colHasInside[x] === 1;
    if (anyInside) envelope1d(bt, col, height, s);

    for (let y = 0; y < height; y++) {
      const i = y * width + x;
      const cov = alpha[i];
      // Only one of the two transforms is non-zero at any pixel: inside pixels seed
      // `a`, outside pixels seed `b`. A column with no glyph is outside everywhere.
      let value = cov >= 128 ? -Math.sqrt(d[y]) : Math.sqrt(colA[y]);

      if (cov > 0 && cov < 255) {
        const c = cov / 255;
        const blend = 1 - Math.min(1, Math.max(0, Math.abs(value) - 1));
        value = value * (1 - blend) + (0.5 - c) * blend;
      }

      sdf[i] = value;
      if (value > maxOutside) maxOutside = value;
      else if (-value > maxInside) maxInside = -value;
    }
  }

  return { sdf, width, height, maxOutside, maxInside };
}
